import type { Vector2 } from "./player-movement";

const fullTurnRadians = Math.PI * 2;

const requireFinite = (value: number, name: string): void => {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be finite`);
  }
};

const requirePositive = (value: number, name: string): void => {
  requireFinite(value, name);
  if (value <= 0) {
    throw new RangeError(`${name} must be positive`);
  }
};

export const normalizeHeading = (headingRadians: number): number => {
  requireFinite(headingRadians, "Heading");
  const normalized =
    ((((headingRadians + Math.PI) % fullTurnRadians) + fullTurnRadians) %
      fullTurnRadians) -
    Math.PI;
  return normalized === -Math.PI ? Math.PI : normalized;
};

export const headingToward = (
  origin: Vector2,
  target: Vector2,
  currentHeadingRadians: number,
): number => {
  requireFinite(origin.x, "Steering origin x");
  requireFinite(origin.y, "Steering origin y");
  requireFinite(target.x, "Steering target x");
  requireFinite(target.y, "Steering target y");
  const differenceX = target.x - origin.x;
  const differenceY = target.y - origin.y;
  return differenceX === 0 && differenceY === 0
    ? normalizeHeading(currentHeadingRadians)
    : Math.atan2(differenceY, differenceX);
};

export const turnTowardHeading = (
  currentHeadingRadians: number,
  targetHeadingRadians: number,
  turnRateRadiansPerSecond: number,
  stepMilliseconds: number,
): number => {
  requirePositive(turnRateRadiansPerSecond, "Turn rate");
  requirePositive(stepMilliseconds, "Steering step");
  const current = normalizeHeading(currentHeadingRadians);
  const target = normalizeHeading(targetHeadingRadians);
  const difference = normalizeHeading(target - current);
  const maximumTurn = turnRateRadiansPerSecond * (stepMilliseconds / 1_000);
  if (Math.abs(difference) <= maximumTurn) {
    return target;
  }
  return normalizeHeading(current + Math.sign(difference) * maximumTurn);
};
